import { css } from '@emotion/core'
import React, { Children, type ReactNode } from 'react'
import { Arrow } from '@fs/zion-icon'
import { Cell, Row } from '@fs/zion-ui'
import { useMergeGridContext } from './providers/MergeGridProvider'

const gridCss = css`
  display: grid;

  & > :first-of-type {
    grid-column: span 2;
  }
`

const hiddenArrowCss = css`
  /** Spacing for the arrow insert from ArrowDecorator. */
  visibility: hidden;
`

const arrowCss = css`
  /** Move Arrow into spacing for arrow in MergeGrid. */
  align-self: center;
  position: relative;
  width: 0;
`

type MergeGridProps = {
  children: ReactNode
  arrowDiv?: ReactNode
}

/** Grid for 2 children, an arrow or space, and the last child where all the children have the same width and height.
 * The arrow or space, and last child are removed in mobile sizes.
 */
export default function MergeGrid({ children, arrowDiv }: MergeGridProps): JSX.Element {
  const { showThirdColumn } = useMergeGridContext()

  const decidingIndex = 1
  return (
    <div
      css={gridCss}
      style={{
        gridTemplateColumns: showThirdColumn ? '1fr 1fr auto 1fr' : '1fr 1fr',
        gap: '0px',
      }}
    >
      {Children.map(children, (child, childIndex) => {
        if (childIndex >= decidingIndex) return null
        // Min width is required to make sure there is no overflow from Ellipsis children.
        return <div style={{ minWidth: 0 }}>{child}</div>
      })}

      {showThirdColumn && (
        <>
          {arrowDiv || (
            <div css={hiddenArrowCss}>
              <Arrow />
            </div>
          )}

          {Children.map(children, (child, childIndex) => {
            // Min width is required to make sure there is no overflow from Ellipsis children.
            if (childIndex === decidingIndex) return <div style={{ minWidth: 0 }}>{child}</div>
            return null
          })}
        </>
      )}
    </div>
  )
}

type ArrowDecoratorProps = {
  children: ReactNode
}

/** Adds Arrow to end of children; to be used with MergeGrid to align the arrow vertically in the middle of children. */
export function ArrowDecorator({ children }: ArrowDecoratorProps): JSX.Element {
  const { showThirdColumn } = useMergeGridContext()

  return (
    <Row gutters="none" alignItemsY="middle">
      <Cell growX>{children}</Cell>
      {showThirdColumn && (
        <div css={arrowCss}>
          <Arrow />
        </div>
      )}
    </Row>
  )
}
